import type { IAction } from "@bbj-types/action";
import type { IAddress } from "@bbj-types/address";
import type { ObjectId } from "@bbj-types/utils";
import type { TMissedReason } from "@complex/Actions/Modal/logics/types";
import type { IDateRange } from "@styleguide";
import { useCallback } from "react";
import * as apiTools from "../../api.tools";
import type { IGetActionsData } from "./getAction";

const axiosV1 = apiTools.buildMakeUseAxios(`${import.meta.env.VITE_ACTIVITY_SERVICE_URL}`);

export interface IUpdateActionBody {
  isAutoGenerate: boolean;
  status: IAction["status"];
  type: string;
  address?: IAddress;
  apply?: ObjectId;
  candidate?: ObjectId;
  challenges?: ObjectId[];
  comments?: string;
  contactChannel?: string;
  deadlineDate?: Date;
  endOfContract?: Date;
  executionDate?: Date;
  jobSituation?: string;
  missedReason?: TMissedReason["value"];
  owner?: ObjectId;
  timeSpent?: number;
  title?: string;
  endDate?: Date | null;
  approach?: ObjectId;
  executionPeriod?: IDateRange; // used for action with approach type, otherwise we use executionDate and endDate
  sendEventToOwner?: boolean;
}

export const usePutAction = () => {
  const [state, execute, cancel] = axiosV1<IGetActionsData, IUpdateActionBody, unknown>(
    {
      url: "/actions",
      method: "PUT",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: ObjectId, data: IUpdateActionBody) => {
      return execute({
        url: `/actions/${id}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

const axiosV2 = apiTools.buildMakeUseAxios(`${import.meta.env.VITE_ACTIVITY_SERVICE_URL}/v2`);

export const useDeleteAction = () => {
  const [state, execute, cancel] = axiosV2<void, void, unknown>(
    {
      url: "/actions",
      method: "DELETE",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: ObjectId) => {
      return execute({
        url: `/actions/${id}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

const axiosAction = apiTools.buildMakeUseAxios(`${import.meta.env.VITE_ACTIVITY_SERVICE_URL}/actions`);

interface IActionTypeTransfer {
  oldActionType: string;
  newActionType: string;
}

interface IActionTypeTransferIds {
  oldActionTypeIds: string[];
  newActionType: string;
}

export const useTransferActionType = () => {
  const [state, execute, cancel] = axiosAction<void, IActionTypeTransfer | IActionTypeTransferIds, unknown>(
    {
      url: "/transferActionType",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: IActionTypeTransfer | IActionTypeTransferIds) => {
      return execute({
        url: "/transferActionType",
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useUpdateAction = () => {
  const [state, execute, cancel] = axiosAction<void, Partial<Omit<IAction, "_id">>, unknown>(
    {
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string, data: Partial<Omit<IAction, "_id">>) => {
      return execute({
        url: `/${id}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export * from "./batchDeleteAction";
export * from "./getAction";
export {
  /*@deprecated */
  useGetActions as useGetAction,
} from "./getAction";
export * from "./patchActionBatch";
